import React, { useMemo, useCallback } from 'react';
import {
  BorderlessTableOutlined,
  FieldNumberOutlined,
  FileSearchOutlined,
  GiftOutlined,
  GoldOutlined,
  MessageOutlined,
  TableOutlined,
  TrophyOutlined,
  SendOutlined,
} from '@ant-design/icons';

import { IGfpOffer } from 'types/api/GfpOffer';
import GfpOfferDetails from 'screens/OfferDetails/components/GfpOffer/GfpOfferDetails';
import { Image } from 'components/UI/Image/Image';

import ProductOffer from 'model/ProductOffer';
import { useBreakpoints } from 'hooks/useMediaQuery';
import * as S from './GfpOffer.styled';
import TypeSaleInput from '../TypeSaleInput/TypeSaleInput';

type IProps = {
  gfpOffer: IGfpOffer;
  productOffer: ProductOffer;
  onImageClick?: (images: string[], title: string) => void;
};

export default function GfpOffer(props: IProps) {
  const { isTabletOrLarger } = useBreakpoints();
  const { gfpOffer, productOffer, onImageClick } = props;

  const productOfferWithGfpLineId = useMemo(
    () => ({
      ...productOffer,
      gfpLineId: gfpOffer.gfpLineId,
    }),
    [productOffer, gfpOffer.gfpLineId],
  );

  const { qualityObservation1, qualityObservation2 } = gfpOffer;

  const gfpObs = qualityObservation1 || qualityObservation2;
  const gfpObsText =
    qualityObservation1 && qualityObservation2
      ? `${qualityObservation1}; ${qualityObservation2}`
      : gfpObs;

  const imagesToDisplay = useMemo(() => {
    const gfpImages = gfpOffer.images?.filter(Boolean) || [];
    const productImages = productOffer.images?.filter(Boolean) || [];
    const defaultImage = productOffer.defaultImage
      ? [productOffer.defaultImage]
      : [];

    return gfpImages.length > 0
      ? gfpImages
      : productImages.length > 0
      ? productImages
      : defaultImage;
  }, [gfpOffer.images, productOffer.images, productOffer.defaultImage]);

  const handleImageClick = useCallback(() => {
    if (imagesToDisplay.length > 0 && onImageClick) {
      onImageClick(imagesToDisplay, `Imagens da GFP: ${productOffer.longName}`);
    }
  }, [imagesToDisplay, productOffer.longName, onImageClick]);

  return (
    <S.OfferContainer>
      <S.OfferInfoContainer>
        <S.ImageContainer>
          <Image
            border="1px solid #D9D9D9"
            size={120}
            src={imagesToDisplay[0]}
            alt={productOffer.longName}
            onClick={handleImageClick}
            style={{ cursor: 'pointer' }}
          />
        </S.ImageContainer>
        <S.DetailsContainer>
          <S.InfosContainer>
            <GfpOfferDetails
              icon={<TrophyOutlined />}
              title="Qualidade:"
              value={productOffer.quality}
              tag
            />
            <GfpOfferDetails
              icon={<SendOutlined />}
              title="Entrega CVH:"
              value={gfpOffer.deliveryDate}
            />
            <GfpOfferDetails
              icon={<GoldOutlined />}
              title="Quant. de Emb.:"
              value={gfpOffer.packingQuantity}
            />
            <GfpOfferDetails
              icon={<GiftOutlined />}
              title="Quant. por Emb.:"
              value={gfpOffer.quantityPerPacking}
            />
            <GfpOfferDetails
              icon={<BorderlessTableOutlined />}
              title="Série:"
              value={gfpOffer.series}
            />
            <GfpOfferDetails
              icon={<FieldNumberOutlined />}
              title="Número da GFP:"
              value={gfpOffer.gfpNumber}
            />
            <GfpOfferDetails
              icon={<TableOutlined />}
              title="Lote:"
              value={gfpOffer.lot}
            />
            {gfpObs && (
              <GfpOfferDetails
                icon={<FileSearchOutlined />}
                title="Observações da GFP:"
                value={gfpObsText}
              />
            )}
          </S.InfosContainer>
          {gfpOffer.observation && (
            <S.ObsContainer>
              <GfpOfferDetails
                icon={<MessageOutlined />}
                title="Observação do Produtor"
                value={gfpOffer.observation}
              />
            </S.ObsContainer>
          )}
        </S.DetailsContainer>
        {isTabletOrLarger && (
          <TypeSaleInput
            gfpOffer={gfpOffer}
            productOffer={productOfferWithGfpLineId}
          />
        )}
      </S.OfferInfoContainer>
      {!isTabletOrLarger && (
        <TypeSaleInput
          gfpOffer={gfpOffer}
          productOffer={productOfferWithGfpLineId}
          priceOnly
        />
      )}
    </S.OfferContainer>
  );
}
